import {
  AUTOMATIC_CALCULATION_ITEMS,
  formatCircumferenceValue,
  formatVisitDateLabel,
  getCatalogByKey,
  isFormLayoutEntry,
  matchCatalogByLabel,
} from "./circumference-utils";
import { buildCompositionProgressChartModel } from "./composition-progress-chart";

export const PROGRESS_PERIODS = [
  { key: "3M", label: "3M", months: 3 },
  { key: "6M", label: "6M", months: 6 },
  { key: "12M", label: "12M", months: 12 },
  { key: "all", label: "Tudo", months: null },
];

const toNumber = (value) => {
  if (value == null || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const parseVisitDate = (visitDate) => {
  if (!visitDate) {
    return null;
  }
  if (visitDate.includes("/")) {
    const [day, month, year] = visitDate.split("/");
    if (!day || !month || !year) {
      return null;
    }
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(`${visitDate}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const sortMeasurementsTimeline = (measurements = []) =>
  [...measurements].sort((a, b) => {
    const dateA = a?.visit_date || "";
    const dateB = b?.visit_date || "";
    if (dateA !== dateB) {
      return dateA.localeCompare(dateB);
    }
    return Number(a?.id || 0) - Number(b?.id || 0);
  });

export const filterCompletedMeasurements = (measurements = []) =>
  sortMeasurementsTimeline(measurements).filter((item) => item?.status !== "DRAFT");

export const toIsoDateString = (visitDate) => {
  const date = parseVisitDate(visitDate);
  if (!date) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getMeasurementDateBounds = (measurements = []) => {
  const completed = filterCompletedMeasurements(measurements);

  if (completed.length === 0) {
    return { startDate: "", endDate: "" };
  }

  const isoDates = completed
    .map((measurement) => toIsoDateString(measurement.visit_date))
    .filter(Boolean)
    .sort();

  return {
    startDate: isoDates[0],
    endDate: isoDates[isoDates.length - 1],
  };
};

export const getAvailableVisitDateOptions = (measurements = []) => {
  const completed = filterCompletedMeasurements(measurements);
  const seen = new Set();
  const options = [];

  completed.forEach((measurement) => {
    const value = toIsoDateString(measurement.visit_date);
    if (!value || seen.has(value)) {
      return;
    }

    seen.add(value);
    options.push({
      value,
      label: formatVisitDateLabel(measurement.visit_date),
    });
  });

  return options.sort((a, b) => a.value.localeCompare(b.value));
};

export const getStartDateOptions = (options = [], endDate = "") => {
  if (!endDate) {
    return options;
  }

  return options.filter((option) => option.value <= endDate);
};

export const getEndDateOptions = (options = [], startDate = "") => {
  if (!startDate) {
    return options;
  }

  return options.filter((option) => option.value >= startDate);
};

export const filterMeasurementsByDateRange = (
  measurements = [],
  startDate = "",
  endDate = ""
) => {
  const completed = filterCompletedMeasurements(measurements);

  if (!startDate && !endDate) {
    return completed;
  }

  return completed.filter((measurement) => {
    const isoDate = toIsoDateString(measurement.visit_date);
    if (!isoDate) {
      return false;
    }
    if (startDate && isoDate < startDate) {
      return false;
    }
    if (endDate && isoDate > endDate) {
      return false;
    }
    return true;
  });
};

export const filterMeasurementsByPeriod = (measurements = [], period = "all") => {
  const completed = filterCompletedMeasurements(measurements);
  if (period === "all" || !period) {
    return completed;
  }

  const periodConfig = PROGRESS_PERIODS.find((item) => item.key === period);
  if (!periodConfig?.months || completed.length === 0) {
    return completed;
  }

  const latestMeasurement = completed[completed.length - 1];
  const latestDate = parseVisitDate(latestMeasurement.visit_date);
  if (!latestDate) {
    return completed;
  }

  const cutoff = new Date(latestDate);
  cutoff.setMonth(cutoff.getMonth() - periodConfig.months);

  return completed.filter((measurement) => {
    const visitDate = parseVisitDate(measurement.visit_date);
    return visitDate && visitDate >= cutoff;
  });
};

const readCircumferenceCm = (measurement, catalogKey) => {
  const catalog = getCatalogByKey(catalogKey);
  if (!catalog) {
    return null;
  }

  if (catalog.persistAs === "circumference" && catalog.apiField) {
    return toNumber(measurement?.circumference?.[catalog.apiField]);
  }

  if (catalog.persistAs === "custom") {
    const customEntry = (measurement?.custom_circumferences || []).find(
      (item) =>
        item?.label &&
        !isFormLayoutEntry(item) &&
        matchCatalogByLabel(item.label)?.key === catalogKey
    );
    return toNumber(customEntry?.value_cm);
  }

  return null;
};

const readAnthropometryField = (measurement, field) =>
  toNumber(measurement?.anthropometry?.[field]);

const readCircumferenceField = (measurement, field) =>
  toNumber(measurement?.circumference?.[field]);

const readWeightKg = (measurement) =>
  toNumber(measurement?.metabolic_data?.weight_kg);

export const SUMMARY_METRICS = [
  {
    key: "weight",
    label: "Peso",
    unit: "kg",
    resolveValue: readWeightKg,
  },
  {
    key: "body_fat",
    label: "% Gordura",
    unit: "%",
    resolveValue: (measurement) => readAnthropometryField(measurement, "body_fat_percentage"),
  },
  {
    key: "lean_mass",
    label: "Massa magra",
    unit: "kg",
    resolveValue: (measurement) => readAnthropometryField(measurement, "lean_mass_kg"),
  },
  {
    key: "fat_mass",
    label: "Gordura",
    unit: "kg",
    resolveValue: (measurement) => readAnthropometryField(measurement, "fat_mass_kg"),
  },
  {
    key: "bmi",
    label: "IMC",
    unit: "",
    resolveValue: (measurement) => readAnthropometryField(measurement, "bmi"),
  },
  {
    key: "rcq",
    label: "RCQ",
    unit: "",
    resolveValue: (measurement) => readCircumferenceField(measurement, "waist_to_hip_ratio"),
  },
  {
    key: "rcest",
    label: "RCEst",
    unit: "",
    resolveValue: (measurement) => readCircumferenceField(measurement, "waist_to_height_ratio"),
  },
  {
    key: "waist",
    label: "Cintura",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "waist"),
  },
  {
    key: "hips",
    label: "Quadril",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "hips"),
  },
  {
    key: "cmb",
    label: "CMB",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "cmb"),
  },
];

export const MONTHLY_METRICS = [
  SUMMARY_METRICS[0],
  SUMMARY_METRICS[1],
  SUMMARY_METRICS[2],
  SUMMARY_METRICS[4],
  SUMMARY_METRICS[7],
  SUMMARY_METRICS[8],
];

export const formatMetricValue = (value, unit) => {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  const formatted = value.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
  return unit ? `${formatted} ${unit}` : formatted;
};

export const formatMetricDelta = (delta, unit) => {
  if (delta == null || !Number.isFinite(delta)) {
    return "—";
  }
  const formatted = delta.toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  });
  return unit ? `${formatted} ${unit}` : formatted;
};

const resolveTrend = (delta) => {
  if (delta == null || !Number.isFinite(delta) || delta === 0) {
    return "flat";
  }
  return delta > 0 ? "up" : "down";
};

export const buildInitialFinalSummary = (measurements = []) => {
  const timeline = filterCompletedMeasurements(measurements);

  if (timeline.length < 2) {
    return {
      items: [],
      initialDate: timeline[0]?.visit_date || null,
      finalDate: timeline[0]?.visit_date || null,
      hasComparison: false,
    };
  }

  const initialMeasurement = timeline[0];
  const finalMeasurement = timeline[timeline.length - 1];

  const items = SUMMARY_METRICS.map((metric) => {
    const initialValue = metric.resolveValue(initialMeasurement);
    const finalValue = metric.resolveValue(finalMeasurement);

    if (initialValue == null || finalValue == null) {
      return null;
    }

    const delta = finalValue - initialValue;
    const deltaPercent =
      initialValue !== 0 ? (delta / initialValue) * 100 : null;

    return {
      key: metric.key,
      label: metric.label,
      unit: metric.unit,
      initialValue,
      finalValue,
      initialLabel: formatMetricValue(initialValue, metric.unit),
      finalLabel: formatMetricValue(finalValue, metric.unit),
      delta,
      deltaLabel: formatMetricDelta(delta, metric.unit),
      deltaPercent,
      deltaPercentLabel:
        deltaPercent == null
          ? null
          : `${deltaPercent >= 0 ? "+" : ""}${deltaPercent.toLocaleString("pt-BR", {
              maximumFractionDigits: 1,
            })}%`,
      trend: resolveTrend(delta),
    };
  }).filter(Boolean);

  return {
    items,
    initialDate: initialMeasurement.visit_date,
    finalDate: finalMeasurement.visit_date,
    hasComparison: items.length > 0,
  };
};

const formatMonthKeyLabel = (monthKey) => {
  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date
    .toLocaleDateString("pt-BR", { month: "short", year: "2-digit" })
    .replace(".", "")
    .replace(/^\w/, (char) => char.toUpperCase());
};

export const buildMonthlySnapshots = (
  measurements = [],
  metrics = MONTHLY_METRICS
) => {
  const timeline = filterCompletedMeasurements(measurements);
  const byMonth = new Map();

  timeline.forEach((measurement) => {
    const visitDate = parseVisitDate(measurement.visit_date);
    if (!visitDate) {
      return;
    }

    const monthKey = `${visitDate.getFullYear()}-${String(visitDate.getMonth() + 1).padStart(2, "0")}`;
    const existing = byMonth.get(monthKey);
    if (!existing) {
      byMonth.set(monthKey, measurement);
      return;
    }

    const existingDate = parseVisitDate(existing.visit_date);
    if (existingDate && visitDate >= existingDate) {
      byMonth.set(monthKey, measurement);
    }
  });

  const monthKeys = [...byMonth.keys()].sort();

  const rows = metrics
    .map((metric) => {
      const cells = monthKeys.map((monthKey) => {
        const measurement = byMonth.get(monthKey);
        const value = metric.resolveValue(measurement);
        return {
          monthKey,
          monthLabel: formatMonthKeyLabel(monthKey),
          value,
          valueLabel: formatMetricValue(value, metric.unit),
        };
      });

      if (!cells.some((cell) => cell.value != null)) {
        return null;
      }

      return {
        key: metric.key,
        label: metric.label,
        unit: metric.unit,
        cells,
      };
    })
    .filter(Boolean);

  return {
    months: monthKeys.map((monthKey) => ({
      key: monthKey,
      label: formatMonthKeyLabel(monthKey),
    })),
    rows,
  };
};

const readAutomaticCalculationValue = (measurement, item) => {
  const sourceData =
    item.source === "circumference"
      ? measurement?.circumference || {}
      : measurement?.anthropometry || {};
  return toNumber(sourceData[item.field]);
};

export const buildAutomaticCalculationTimeline = (measurements = []) => {
  const timeline = filterCompletedMeasurements(measurements);

  return AUTOMATIC_CALCULATION_ITEMS.map((item) => {
    const points = timeline
      .map((measurement) => {
        const value = readAutomaticCalculationValue(measurement, item);
        if (value == null) {
          return null;
        }
        return {
          visitDate: measurement.visit_date,
          value,
        };
      })
      .filter(Boolean);

    if (points.length === 0) {
      return null;
    }

    const lastPoint = points[points.length - 1];
    const previousPoint = points.length > 1 ? points[points.length - 2] : null;
    const delta =
      previousPoint != null ? lastPoint.value - previousPoint.value : null;

    return {
      key: item.key,
      label: item.label,
      timeline: points,
      lastValue: lastPoint.value,
      lastValueLabel: formatCircumferenceValue(lastPoint.value),
      delta,
      deltaLabel: formatMetricDelta(delta, ""),
      trend: resolveTrend(delta),
    };
  }).filter(Boolean);
};

export const buildCompositionTimeline = (measurements = []) =>
  buildCompositionProgressChartModel(measurements);

export const formatProgressDateLabel = (visitDate) => formatVisitDateLabel(visitDate);

const hasCompositionValues = (measurement) => {
  const lean = toNumber(measurement?.anthropometry?.lean_mass_kg);
  const fat = toNumber(measurement?.anthropometry?.fat_mass_kg);
  return (lean != null && lean > 0) || (fat != null && fat > 0);
};

export const getCompositionVisitOptions = (measurements = []) => {
  const seen = new Set();
  const options = [];

  filterCompletedMeasurements(measurements).forEach((measurement) => {
    if (!hasCompositionValues(measurement)) {
      return;
    }

    const value = toIsoDateString(measurement.visit_date);
    if (!value || seen.has(value)) {
      return;
    }

    seen.add(value);
    options.push({
      value,
      label: formatVisitDateLabel(measurement.visit_date),
    });
  });

  return options.sort((a, b) => a.value.localeCompare(b.value));
};

export const findMeasurementByVisitDate = (measurements = [], visitDate = "") => {
  if (!visitDate) {
    return null;
  }

  const matches = filterCompletedMeasurements(measurements).filter(
    (measurement) => toIsoDateString(measurement.visit_date) === visitDate
  );

  return matches.length > 0 ? matches[matches.length - 1] : null;
};

export const buildCompositionDonutModel = (measurement) => {
  const lean = toNumber(measurement?.anthropometry?.lean_mass_kg) ?? 0;
  const fat = toNumber(measurement?.anthropometry?.fat_mass_kg) ?? 0;
  const total = lean + fat;

  if (total <= 0) {
    return {
      hasValues: false,
      lean,
      fat,
      total,
      chartData: { labels: [], datasets: [] },
      chartOptions: {},
    };
  }

  return {
    hasValues: true,
    lean,
    fat,
    total,
    chartData: {
      labels: ["Massa magra", "Gordura"],
      datasets: [
        {
          data: [lean, fat],
          backgroundColor: ["rgba(74, 222, 128, 1)", "rgba(251, 113, 133, 1)"],
          borderColor: ["#FFFFFF", "#FFFFFF"],
          borderWidth: 3,
          hoverOffset: 6,
        },
      ],
    },
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            boxHeight: 8,
            padding: 16,
            color: "#6B7280",
            font: { size: 13, weight: "500" },
          },
        },
        tooltip: {
          backgroundColor: "#1F1F23",
          titleColor: "#FFFFFF",
          bodyColor: "#FFFFFF",
          padding: 12,
          cornerRadius: 10,
          callbacks: {
            label: (context) => {
              const value = context.parsed;
              const percent = total > 0 ? Math.round((value / total) * 100) : 0;
              return `${context.label}: ${value.toLocaleString("pt-BR", {
                maximumFractionDigits: 1,
              })} kg (${percent}%)`;
            },
          },
        },
      },
    },
  };
};
